import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Search, ExternalLink, Bookmark, Sparkles, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/AppLayout";
import { LevelBadge } from "@/components/Badges";
import { api, formatErr, usePoll } from "@/lib/api";
import { INDUSTRIES, STATES } from "@/lib/constants";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FundsBadge, PrepareBar, PrepBoard } from "@/components/grants/Prep";

const LEVELS = ["federal", "state", "county", "city"];

function GrantCard({ g, industry, testid, score, picked, onPick }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const map = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/grants/saved", { grant: g, industry });
      if (["none", "error"].includes(data.roadmap_status)) await api.post(`/grants/saved/${data.grant_id}/roadmap`, { industry });
      navigate(`/dashboard/grants/${data.grant_id}`);
    } catch (e) {
      toast.error(formatErr(e));
      setBusy(false);
    }
  };
  return (
    <div className="card-lift bg-white border border-slate-200 rounded-lg p-6 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap">
        <LevelBadge level={g.level} />
        {!g.verified_source && <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">AI researched</span>}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid={`${testid}-prep-label`}><Checkbox checked={picked} onCheckedChange={onPick} data-testid={`${testid}-prep-checkbox`} />Prepare</label>
        {score && <span title={score.reason} data-testid={`${testid}-match-score`} className={`text-xs font-bold px-2 py-0.5 rounded-full ${score.score >= 75 ? "bg-emerald-600 text-white" : score.score >= 50 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`}>{score.score}% match</span>}
      </div>
      {score?.reason && <p className="text-[11px] text-slate-400 mt-1.5">{score.reason}</p>}
      <h3 className="font-semibold text-slate-900 mt-3 leading-snug">{g.title}</h3>
      <p className="text-sm text-slate-500 mt-1">{g.agency}</p>
      {g.description && <p className="text-sm text-slate-600 mt-3 line-clamp-3">{g.description}</p>}
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-500">
        {g.amount && <div><span className="block font-mono uppercase tracking-widest text-[10px] text-slate-400">Amount</span>{g.amount}</div>}
        {!g.amount && g.award_ceiling && !/^[0,.$\s]*$/.test(g.award_ceiling) && <div><span className="block font-mono uppercase tracking-widest text-[10px] text-slate-400">Up to</span>{g.award_ceiling}</div>}
        <div><span className="block font-mono uppercase tracking-widest text-[10px] text-slate-400">Deadline</span>{g.deadline}</div>
      </div>
      <div className="mt-3"><FundsBadge match={g.cost_sharing} upfront={g.upfront_funds} testid={`${testid}-funds`} /></div>
      <div className="mt-5 pt-4 border-t border-slate-100 flex gap-2 mt-auto">
        <Button size="sm" onClick={map} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${testid}-map-btn`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}Map steps
        </Button>
        {g.url && (
          <a href={g.url} target="_blank" rel="noreferrer" data-testid={`${testid}-source-link`}>
            <Button size="sm" variant="outline"><ExternalLink className="h-3.5 w-3.5 mr-1.5" />Source</Button>
          </a>
        )}
      </div>
    </div>
  );
}

function SearchForm({ onResult }) {
  const [qs] = useSearchParams();
  const [f, setF] = useState({ keyword: qs.get("q") || "", industry: "Small Business", state: "", county: "", city: "", levels: [...LEVELS] });
  const [busy, setBusy] = useState(false);
  const toggle = (lv) => setF({ ...f, levels: f.levels.includes(lv) ? f.levels.filter((x) => x !== lv) : [...f.levels, lv] });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.levels.length) return toast.error("Pick at least one grant level");
    if (f.levels.some((l) => l !== "federal") && !f.state) return toast.error("Choose a state for state, county or city grants");
    setBusy(true);
    try {
      const { data } = await api.post("/grants/search", f);
      onResult(data, f.industry);
    } catch (e2) {
      toast.error(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="grant-search-form">
      <div className="md:col-span-5"><Label>Keyword</Label><Input className="mt-1.5 h-11" placeholder="e.g. workforce training, solar, rural health" value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} data-testid="grant-search-keyword-input" /></div>
      <div className="md:col-span-3"><Label>Industry</Label>
        <Select value={f.industry} onValueChange={(v) => setF({ ...f, industry: v })}>
          <SelectTrigger className="mt-1.5 h-11" data-testid="grant-search-industry-select"><SelectValue /></SelectTrigger>
          <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-4"><Label>State</Label>
        <Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}>
          <SelectTrigger className="mt-1.5 h-11" data-testid="grant-search-state-select"><SelectValue placeholder="Select a state" /></SelectTrigger>
          <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-3"><Label>County</Label><Input className="mt-1.5 h-11" placeholder="Optional" value={f.county} onChange={(e) => setF({ ...f, county: e.target.value })} data-testid="grant-search-county-input" /></div>
      <div className="md:col-span-3"><Label>City</Label><Input className="mt-1.5 h-11" placeholder="Optional" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="grant-search-city-input" /></div>
      <div className="md:col-span-4 flex items-end gap-4 pb-3 flex-wrap">
        {LEVELS.map((lv) => (
          <label key={lv} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
            <Checkbox checked={f.levels.includes(lv)} onCheckedChange={() => toggle(lv)} data-testid={`grant-level-${lv}-checkbox`} />{lv}
          </label>
        ))}
      </div>
      <div className="md:col-span-2 flex items-end">
        <Button type="submit" disabled={busy} className="w-full h-11 bg-slate-900 hover:bg-slate-800" data-testid="grant-search-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" />Search</>}
        </Button>
      </div>
    </form>
  );
}

const matchRank = (m) => (m === false ? 0 : m == null ? 1 : 2);

function Results({ result, industry, pick }) {
  const [local, setLocal] = useState(result);
  const [noMatch, setNoMatch] = useState(false);
  useEffect(() => setLocal(result), [result]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { pick.refresh([...(local.federal || []), ...(local.local || [])]); }, [local]);
  const poll = useCallback(async () => {
    const { data } = await api.get(`/grants/search/${result.search_id}`);
    setLocal(data);
  }, [result.search_id]);
  usePoll(poll, local.local_status === "pending" || local.score_status === "pending" || local.funds_status === "pending", 3000);
  const { hasPkg } = useAuth();
  const scores = local.scores || {};
  const runScore = async () => {
    try { await api.post(`/grants/search/${result.search_id}/score`); setLocal({ ...local, score_status: "pending" }); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const byScore = (arr) => (local.score_status === "ready" ? [...arr].sort((a, b) => (scores[b.source_id]?.score || 0) - (scores[a.source_id]?.score || 0)) : arr);
  const sortByScore = (arr) => (noMatch ? byScore(arr).filter((g) => g.cost_sharing !== true).sort((a, b) => matchRank(a.cost_sharing) - matchRank(b.cost_sharing)) : byScore(arr));
  const card = (g, i, kind) => <GrantCard key={g.source_id} g={g} industry={industry} score={scores[g.source_id]} testid={`${kind}-grant-card-${i}`} picked={pick.has(g.source_id)} onPick={() => pick.toggle(g)} />;
  return (
    <div className="mt-10 space-y-12">
      <div className="space-y-3">
        <div className="bg-slate-900 text-white rounded-lg p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid="match-score-bar">
          <div className="flex-1"><div className="font-semibold">Grant Success Predictor</div><div className="text-sm text-slate-300">AI match score for every listing, based on your Company profile's location, industry, size and structure.</div></div>
          {hasPkg("proposal_writer") ? (
            <Button onClick={runScore} disabled={local.score_status === "pending" || local.local_status === "pending"} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="score-matches-button">
              {local.score_status === "pending" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{local.score_status === "ready" ? "Re-score matches" : "Score my matches"}
            </Button>
          ) : <span className="text-xs text-slate-400">Included with Proposal Writer</span>}
        </div>
        <label className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg p-4 cursor-pointer" data-testid="no-match-filter">
          <Checkbox checked={noMatch} onCheckedChange={(v) => setNoMatch(!!v)} className="mt-0.5" data-testid="no-match-filter-checkbox" />
          <span className="text-sm text-slate-700"><b>Hide grants that require matching funds</b><span className="block text-xs text-slate-500">Shows grants that don't ask you to put in your own money first. Confirmed ones come first, then grants with no listed requirement.{local.funds_status === "pending" ? " Checking Grants.gov…" : ""}</span></span>
        </label>
      </div>
      {result.query.levels.includes("federal") && (
        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl font-bold text-slate-900">Federal opportunities</h2>
            <span className="text-sm text-slate-500" data-testid="federal-results-count">{local.federal.length} live from Grants.gov</span>
          </div>
          {local.federal.length === 0 ? <p className="text-slate-500 text-sm">No open federal listings matched. Try a broader keyword.</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortByScore(local.federal).map((g, i) => card(g, i, "federal"))}
            </div>
          )}
        </section>
      )}
      {local.local_status !== "skipped" && (
        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl font-bold text-slate-900">State, county & city programs</h2>
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400">AI researched · verify with agency</span>
          </div>
          {local.local_status === "pending" && (
            <div className="bg-white border border-dashed border-emerald-300 rounded-lg p-8 flex items-center gap-4" data-testid="local-results-loading">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
              <div><div className="font-semibold text-slate-900">Researching local programs…</div><div className="text-sm text-slate-500">This usually takes 30–90 seconds.</div></div>
            </div>
          )}
          {local.local_status === "error" && <p className="text-red-600 text-sm flex items-center gap-2" data-testid="local-results-error"><AlertTriangle className="h-4 w-4" />Local research failed. Please search again.</p>}
          {local.local_status === "ready" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortByScore(local.local).map((g, i) => card(g, i, "local"))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

const savedMatch = (s) => s.roadmap?.funds_needed?.match_required ?? s.grant.cost_sharing;

function SavedList({ pick, prepIds }) {
  const [items, setItems] = useState(null);
  const [noMatch, setNoMatch] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { api.get("/grants/saved").then((r) => setItems(r.data)); }, []);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600 mt-8" />;
  if (!items.length) return <p className="text-slate-500 mt-8" data-testid="saved-grants-empty">No saved grants yet. Search and click "Map steps" on any grant.</p>;
  const shown = noMatch ? items.filter((s) => savedMatch(s) !== true).sort((a, b) => matchRank(savedMatch(a)) - matchRank(savedMatch(b))) : items;
  return (
    <>
      <label className="mt-6 flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="saved-no-match-filter"><Checkbox checked={noMatch} onCheckedChange={(v) => setNoMatch(!!v)} data-testid="saved-no-match-checkbox" />Hide grants that require matching funds</label>
      <div className="mt-3 divide-y divide-slate-200 bg-white border border-slate-200 rounded-lg">
        {shown.map((s) => (
          <div key={s.grant_id} className="p-4 sm:p-5 flex items-center gap-3 sm:gap-4 hover:bg-slate-50 transition-colors" data-testid={`saved-grant-row-${s.grant_id}`}>
            {prepIds.includes(s.grant_id) ? <span className="text-[10px] font-bold uppercase text-emerald-700 shrink-0" data-testid={`saved-grant-in-prep-${s.grant_id}`}>Preparing</span>
              : <Checkbox checked={pick.has(s.grant_id)} onCheckedChange={() => pick.toggle(s)} aria-label="Select to prepare" data-testid={`saved-grant-prep-checkbox-${s.grant_id}`} />}
            <button onClick={() => navigate(`/dashboard/grants/${s.grant_id}`)} data-testid={`saved-grant-${s.grant_id}`} className="flex-1 min-w-0 text-left flex items-center gap-3">
              <Bookmark className="h-4 w-4 text-emerald-600 shrink-0 hidden sm:block" />
              <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900 truncate">{s.grant.title}</div><div className="text-sm text-slate-500 truncate">{s.grant.agency}{s.grant.deadline ? ` · ${s.grant.deadline}` : ""}</div>
                <div className="mt-1"><FundsBadge match={savedMatch(s)} upfront={s.roadmap?.funds_needed?.minimum_upfront_funds || s.grant.upfront_funds} testid={`saved-grant-funds-${s.grant_id}`} /></div></div>
              <LevelBadge level={s.grant.level} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

function usePick(room) {
  const [m, setM] = useState({});
  return {
    count: Object.keys(m).length, values: Object.values(m), clear: () => setM({}), has: (k) => k in m,
    refresh: (list) => setM((cur) => {
      const hits = list.filter((g) => g.source_id in cur && cur[g.source_id] !== g);
      return hits.length ? { ...cur, ...Object.fromEntries(hits.map((g) => [g.source_id, g])) } : cur;
    }),
    toggle: (item) => {
      const k = item.grant_id || item.source_id;
      if (k in m) { const rest = { ...m }; delete rest[k]; setM(rest); return; }
      if (Object.keys(m).length >= room) { toast.error(room <= 0 ? "Your preparation list is full. Remove a grant on the Prepare tab or upgrade your plan." : `Your plan has room for ${room} more grant${room === 1 ? "" : "s"} right now.`); return; }
      setM({ ...m, [k]: item });
    },
  };
}

export default function GrantSearch() {
  const [result, setResult] = useState(() => JSON.parse(sessionStorage.getItem("gv_last_search") || "null"));
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "prepare" ? "prepare" : "search"));
  const [prep, setPrep] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadPrep = useCallback(() => api.get("/grant-prep").then((r) => setPrep(r.data)).catch(() => {}), []);
  useEffect(() => { loadPrep(); }, [loadPrep]);
  usePoll(loadPrep, !!prep && (["waiting", "building"].includes(prep.checklist_status) || ["waiting", "building"].includes(prep.funds_status) || prep.grants.some((g) => ["queued", "generating"].includes(g.proposal?.status))), 4000);
  const room = prep ? prep.limit - prep.grants.length : 0;
  const searchPick = usePick(room);
  const savedPick = usePick(room);
  const active = tab === "saved" ? savedPick : searchPick;
  const onResult = (data, industry) => {
    const r = { ...data, industry };
    sessionStorage.setItem("gv_last_search", JSON.stringify(r));
    setResult(r);
  };
  const prepare = async () => {
    setBusy(true);
    try {
      const body = tab === "saved" ? { grant_ids: savedPick.values.map((s) => s.grant_id) } : { grants: searchPick.values, industry: result?.industry || "" };
      const { data } = await api.post("/grant-prep/grants", body);
      setPrep(data); active.clear(); setTab("prepare");
      toast.success("Preparing your grants. Each roadmap takes 1–2 minutes, and deadlines were added to your calendar.");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { setPrep((await api.delete(`/grant-prep/grants/${id}`)).data); } catch (e) { toast.error(formatErr(e)); } };
  const tick = async (key, done) => {
    setPrep((p) => ({ ...p, checklist: p.checklist.map((x) => (x.key === key ? { ...x, done } : x)) }));
    try { await api.put(`/grant-prep/checklist/${key}`, { done }); } catch (e) { toast.error(formatErr(e)); loadPrep(); }
  };
  const rebuild = async () => { try { setPrep((await api.post("/grant-prep/checklist/rebuild")).data); } catch (e) { toast.error(formatErr(e)); } };
  const rebuildFunds = async () => { try { setPrep((await api.post("/grant-prep/funds-plan/rebuild")).data); } catch (e) { toast.error(formatErr(e)); } };
  return (
    <div className="pb-24">
      <PageHeader eyebrow="Grant Finder" title="Find grants at every level">
        Live federal listings from Grants.gov, plus AI research on state, county and city programs for your industry.
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="search" data-testid="tab-search">Search</TabsTrigger><TabsTrigger value="saved" data-testid="tab-saved">Saved grants</TabsTrigger>
          <TabsTrigger value="prepare" data-testid="tab-prepare">Prepare{prep ? ` (${prep.grants.length}/${prep.limit})` : ""}</TabsTrigger></TabsList>
        <TabsContent value="search" className="mt-6">
          <SearchForm onResult={onResult} />
          {result && <Results result={result} industry={result.industry} pick={searchPick} />}
        </TabsContent>
        <TabsContent value="saved"><SavedList pick={savedPick} prepIds={prep?.grants.map((g) => g.grant_id) || []} /></TabsContent>
        <TabsContent value="prepare"><PrepBoard prep={prep} onRemove={remove} onTick={tick} onRebuild={rebuild} onReload={loadPrep} onFundsRebuild={rebuildFunds} onEmail={(on) => setPrep((p) => ({ ...p, weekly_email: on }))} /></TabsContent>
      </Tabs>
      {tab !== "prepare" && prep && <PrepareBar count={active.count} room={room} limit={prep.limit} plan={prep.plan} busy={busy} onPrepare={prepare} onClear={active.clear} />}
    </div>
  );
}
